import React, { useState } from 'react';
import {
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MapIcon,
  CogIcon,
  CheckCircleIcon,
  XMarkIcon,
  SwatchIcon,
  CurrencyDollarIcon,
  LightBulbIcon,
  ComputerDesktopIcon,
  BoltIcon,
  WindowIcon,
  ArchiveBoxIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import GroupSeatManagement from './GroupSeatManagement';

// DDD Imports
import { useLayoutData } from '../use-cases';
import { layoutService } from '../services';
import { ZONE_COLORS } from '../utils/layoutRenderers';
import type { Zone, Seat, SeatAttributes, ZoneCreateRequest } from '../models';

interface SeatCreateRequest {
  seat_number: number;
  zone_id: string;
  seat_type: string;
  position_x: number;
  position_y: number;
  direction: string;
  attributes: SeatAttributes;
}

const SeatConfiguration: React.FC = () => {
  // DDD Hook
  const { zones, seats, loading, error, loadAllData, createZone, updateZone, deleteZone, deleteSeat } = useLayoutData();

  // Local UI State
  const [activeTab, setActiveTab] = useState<'zones' | 'seats' | 'groups'>('zones');
  const [showZoneModal, setShowZoneModal] = useState(false);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [editingSeat, setEditingSeat] = useState<Seat | null>(null);

  // Form State
  const [zoneForm, setZoneForm] = useState<ZoneCreateRequest>({
    room_id: '',
    name: '',
    description: '',
    color: '#3B82F6',
    premium_rate: 1.0,
  });

  const [seatForm, setSeatForm] = useState<SeatCreateRequest>({
    seat_number: 1,
    zone_id: '',
    seat_type: 'standard',
    position_x: 0,
    position_y: 0,
    direction: 'north',
    attributes: {
      has_power_outlet: true,
      has_desk_lamp: true,
      is_window_seat: false,
      partition_height: 'medium',
      monitor_compatible: false,
      has_storage: false,
    },
  });

  // Load data on mount
  React.useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Event Handlers
  const handleZoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = editingZone ? await updateZone(editingZone.id, zoneForm) : await createZone(zoneForm);

    if (success) {
      setShowZoneModal(false);
      setEditingZone(null);
      setZoneForm({ room_id: '', name: '', description: '', color: '#3B82F6', premium_rate: 1.0 });
    }
  };

  const handleDeleteZone = async (zoneId: string) => {
    if (window.confirm('이 구역을 삭제하시겠습니까?')) {
      await deleteZone(zoneId);
    }
  };

  const handleDeleteSeat = async (seatNumber: number) => {
    if (window.confirm(`좌석 ${seatNumber}번을 삭제하시겠습니까?`)) {
      await deleteSeat(seatNumber);
    }
  };

  const openEditZoneModal = (zone: Zone) => {
    setEditingZone(zone);
    setZoneForm({
      room_id: zone.room_id || '',
      name: zone.name,
      description: zone.description || '',
      color: zone.color || '#3B82F6',
      premium_rate: zone.premium_rate,
    });
    setShowZoneModal(true);
  };

  const openNewZoneModal = () => {
    setEditingZone(null);
    setZoneForm({ room_id: '', name: '', description: '', color: '#3B82F6', premium_rate: 1.0 });
    setShowZoneModal(true);
  };

  // Render Functions
  const renderZonesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">구역 관리</h3>
        <button
          onClick={openNewZoneModal}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <PlusIcon className="h-5 w-5" />
          구역 추가
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {zones.map((zone) => (
          <div key={zone.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded" style={{ backgroundColor: zone.color || '#6366F1' }} />
                <span className="font-medium">{zone.name}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEditZoneModal(zone)} className="text-blue-600 hover:text-blue-800">
                  <PencilIcon className="h-4 w-4" />
                </button>
                <button onClick={() => handleDeleteZone(zone.id)} className="text-red-600 hover:text-red-800">
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            {zone.description && <p className="text-sm text-gray-600 mb-2">{zone.description}</p>}
            <div className="text-sm text-gray-500">프리미엄 배율: {zone.premium_rate}x</div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSeatsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">좌석 설정</h3>
        <button
          onClick={() => setShowSeatModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <PlusIcon className="h-5 w-5" />
          좌석 추가
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">좌석 번호</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">구역</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">타입</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">속성</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {seats.map((seat) => {
              const zone = zones.find((z) => z.id === seat.zone_id);
              return (
                <tr key={seat.seat_number}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{seat.seat_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{zone?.name || seat.zone_id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {seat.seat_type === 'premium' && <LightBulbIcon className="h-4 w-4 inline" />}
                    {seat.seat_type === 'deluxe' && <ComputerDesktopIcon className="h-4 w-4 inline" />}
                    {seat.seat_type || 'standard'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-1">
                      {seat.attributes?.has_power_outlet && <BoltIcon className="h-4 w-4 text-yellow-600" />}
                      {seat.attributes?.is_window_seat && <WindowIcon className="h-4 w-4 text-blue-600" />}
                      {seat.attributes?.has_storage && <ArchiveBoxIcon className="h-4 w-4 text-gray-600" />}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {seat.is_active ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">활성</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">비활성</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button onClick={() => handleDeleteSeat(seat.seat_number)} className="text-red-600 hover:text-red-800">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderGroupsTab = () => (
    <div className="space-y-6">
      <GroupSeatManagement />
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">좌석 설정</h2>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('zones')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'zones' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600'}`}
          >
            구역
          </button>
          <button
            onClick={() => setActiveTab('seats')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'seats' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-600'}`}
          >
            좌석
          </button>
          <button
            onClick={() => setActiveTab('groups')}
            className={`px-4 py-2 border-b-2 ${activeTab === 'groups' ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-600'}`}
          >
            그룹 좌석
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'zones' && renderZonesTab()}
        {activeTab === 'seats' && renderSeatsTab()}
        {activeTab === 'groups' && renderGroupsTab()}
      </div>

      {/* Zone Modal */}
      {showZoneModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{editingZone ? '구역 수정' : '구역 추가'}</h3>
            <form onSubmit={handleZoneSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">구역 이름</label>
                <input
                  type="text"
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">설명</label>
                <textarea
                  value={zoneForm.description}
                  onChange={(e) => setZoneForm({ ...zoneForm, description: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">색상</label>
                <div className="grid grid-cols-5 gap-2">
                  {ZONE_COLORS.map((colorItem) => (
                    <button
                      key={colorItem.value}
                      type="button"
                      onClick={() => setZoneForm({ ...zoneForm, color: colorItem.value })}
                      className={`w-10 h-10 rounded border-2 ${zoneForm.color === colorItem.value ? 'border-black' : 'border-gray-300'}`}
                      style={{ backgroundColor: colorItem.value }}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">프리미엄 배율</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="5.0"
                  value={zoneForm.premium_rate}
                  onChange={(e) => setZoneForm({ ...zoneForm, premium_rate: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowZoneModal(false);
                    setEditingZone(null);
                  }}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  취소
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingZone ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeatConfiguration;
